// 🌐 Configuración de i18next - Sistema de Internacionalización
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Importar traducciones
import translationES from './locales/es/translation.json';
import translationEN from './locales/en/translation.json';
import translationPT from './locales/pt/translation.json';
// Archivos por módulo (namespaces): las pantallas reutilizadas por TrackConstruccion.
// En español conservan el texto original del ERP; en inglés, la traducción.
import contabilidadES from './locales/es/contabilidad.json';
import contabilidadEN from './locales/en/contabilidad.json';
import bancosES from './locales/es/bancos.json';
import bancosEN from './locales/en/bancos.json';
import activofijoES from './locales/es/activofijo.json';
import activofijoEN from './locales/en/activofijo.json';
import inventarioES from './locales/es/inventario.json';
import inventarioEN from './locales/en/inventario.json';
import adminES from './locales/es/admin.json';
import adminEN from './locales/en/admin.json';

// Idiomas por instalación: VITE_DEFAULT_LANG (p. ej. 'en') y VITE_LANGS (p. ej. 'en,es').
const IDIOMAS_TODOS = ['es', 'en', 'pt'] as const;
const IDIOMAS_HABILITADOS: string[] = (import.meta.env.VITE_LANGS as string | undefined)?.split(',').map((x) => x.trim()).filter((x) => (IDIOMAS_TODOS as readonly string[]).includes(x)) ?? [...IDIOMAS_TODOS];
const IDIOMA_DEFECTO: string = (import.meta.env.VITE_DEFAULT_LANG as string | undefined) && IDIOMAS_HABILITADOS.includes(import.meta.env.VITE_DEFAULT_LANG as string)
  ? (import.meta.env.VITE_DEFAULT_LANG as string) : (IDIOMAS_HABILITADOS[0] ?? 'es');

// Configuración de recursos
const resources = {
  es: {
    translation: translationES,
    contabilidad: contabilidadES, bancos: bancosES, activofijo: activofijoES, inventario: inventarioES, admin: adminES,
  },
  en: {
    translation: translationEN,
    contabilidad: contabilidadEN, bancos: bancosEN, activofijo: activofijoEN, inventario: inventarioEN, admin: adminEN,
  },
  pt: {
    translation: translationPT,
  },
};

i18n
  // Detectar idioma del navegador
  .use(LanguageDetector)
  // Pasar la instancia i18n a react-i18next
  .use(initReactI18next)
  // Inicializar i18next
  .init({
    resources,
    ns: ['translation', 'contabilidad', 'bancos', 'activofijo', 'inventario', 'admin'],
    defaultNS: 'translation',
    // Sin traducción en el idioma activo → se usa el español original del ERP.
    fallbackNS: 'translation',
    // Idioma por defecto y los habilitados se definen por INSTALACIÓN (build):
    // ERP Ecuador → es (es,en,pt); TrackConstruccion (EE. UU.) → en (en,es).
    // Una clave sin traducir cae al idioma por defecto y, al final, al español original.
    fallbackLng: IDIOMA_DEFECTO === 'es' ? 'es' : [IDIOMA_DEFECTO, 'es'],
    supportedLngs: IDIOMAS_HABILITADOS,
    nonExplicitSupportedLngs: true,   // en-US → en, es-419 → es
    debug: import.meta.env.DEV, // Debug solo en desarrollo

    interpolation: {
      escapeValue: false, // React ya escapa por defecto
    },

    detection: {
      // Orden de detección del idioma
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    react: {
      useSuspense: false,
    },
  });

export default i18n;

// Tipos para autocompletado
export type SupportedLanguages = 'es' | 'en' | 'pt';

const TODOS: Record<SupportedLanguages, { name: string; flag: string }> = {
  es: { name: 'Español', flag: '🇪🇸' },
  en: { name: 'English', flag: import.meta.env.VITE_DEFAULT_LANG === 'en' ? '🇺🇸' : '🇬🇧' },
  pt: { name: 'Português', flag: '🇧🇷' },
};
// Solo los idiomas habilitados en esta instalación (el selector muestra estos).
export const languages = Object.fromEntries(
  Object.entries(TODOS).filter(([k]) => IDIOMAS_HABILITADOS.includes(k)),
) as Record<SupportedLanguages, { name: string; flag: string }>;
export const idiomaPorDefecto = IDIOMA_DEFECTO as SupportedLanguages;
